'use strict'

const Big = require('big.js').default
const debug = require('debug')('metronome-utils:chain-link')
const pTap = require('p-tap')
// Data feeds can be updated from https://cl-docs-addresses.web.app/addresses.json
const dataFeeds = require('./data-feeds.json')
const createUniswapRouter = require('./uniswap')
const dataFeedAggregatorAbi = require('./abi/data-feed-aggregator-v3.json')
const swapFlashLoanAbi = require('./abi/swap-flash-loan.json')
const { fromUnit, handleForkedChainId, toUnit } = require('./utils')
const { findTokenBySymbol } = require('token-list')
const { findByChainId } = require('chain-list')

const dataFeedsMappings = {
  1: ['ethereum-addresses', 'Ethereum Mainnet'],
  137: ['matic-addresses', 'Polygon Mainnet'],
  43114: ['avalanche-price-feeds', 'Avalanche Mainnet']
}

const swapFlashLoanAddress = '0x13cc34aa8037f722405285ad2c82fe570bfa2bdc'

const handleAvalancheAsset = asset => asset.split('.e')[0]

const createChainLink = function (web3) {
  const getTokenRate = function (_fromToken, _toToken, defaultBlock) {
    const fromToken = handleAvalancheAsset(_fromToken)
    const toToken = handleAvalancheAsset(_toToken)
    const chainIdPromise = web3.eth.getChainId().then(handleForkedChainId)

    const getPriceThroughUniswapRouter = function () {
      const uniswapToToken = toToken === 'USD' ? 'USDC' : toToken
      const uniswapFromToken = fromToken === 'USD' ? 'USDC' : fromToken
      debug(
        'Using UniswapRouter to retrieve %s/%s rate as fallback.',
        uniswapFromToken,
        uniswapToToken
      )
      const uniswapRouter = createUniswapRouter(web3)
      return uniswapRouter
        .getTokenRate(
          uniswapFromToken === 'ETH' ? 'WETH' : uniswapFromToken,
          uniswapToToken,
          defaultBlock
        )
        .then(rate =>
          chainIdPromise.then(function (chainId) {
            const { nativeTokenSymbol, wrappedToken } = findByChainId(chainId)
            const destinationToken =
              uniswapToToken === nativeTokenSymbol
                ? wrappedToken.symbol
                : uniswapToToken
            const token = findTokenBySymbol(destinationToken, chainId)
            if (rate === '0' || !token) {
              return { rate, decimals: 0 }
            }
            return { rate, decimals: token.decimals }
          })
        )
    }

    const getDataFeedPair = function (chainId) {
      const [dataFeedFieldName, networkName] = dataFeedsMappings[chainId]
      const { proxies } = dataFeeds[dataFeedFieldName].networks.find(
        n => n.name === networkName
      )
      const pair = `${fromToken.toUpperCase()} / ${toToken}`
      return proxies.find(proxy => proxy.pair === pair)
    }

    return chainIdPromise.then(function (chainId) {
      if (fromToken === 'FRAXBP' && chainId === 1) {
        const fraxbpTokenData = findTokenBySymbol(fromToken, chainId)
        const toTokenData = findTokenBySymbol(toToken, chainId)
        const swapFlashLoanContract = new web3.eth.Contract(
          swapFlashLoanAbi,
          swapFlashLoanAddress
        )
        return Promise.all([
          swapFlashLoanContract.methods.getVirtualPrice().call(),
          getTokenRate(toToken, 'USD', defaultBlock)
        ]).then(([fraxbpUsdPrice, toTokenUsdRate]) => ({
          rate: toUnit(
            Big(fromUnit(fraxbpUsdPrice, fraxbpTokenData.decimals)).div(
              fromUnit(toTokenUsdRate.rate, toTokenUsdRate.decimals)
            ),
            toTokenData.decimals
          ),
          decimals: toTokenData.decimals
        }))
      }
      const { proxy, decimals } = getDataFeedPair(chainId, fromToken) || {}
      if (!proxy) {
        return getPriceThroughUniswapRouter()
      }
      const priceFeed = new web3.eth.Contract(dataFeedAggregatorAbi, proxy)
      return priceFeed.methods
        .latestRoundData()
        .call()
        .then(({ answer }) => answer)
        .then(
          pTap(rate =>
            debug(
              'Rate %s/%s obtained by ChainLink data feed %s is %s',
              fromToken,
              toToken,
              proxy,
              fromUnit(rate, decimals)
            )
          )
        )
        .then(rate => ({ rate, decimals }))
        .catch(getPriceThroughUniswapRouter)
    })
  }

  const getUsdRate = (fromToken, defaultBlock) =>
    getTokenRate(fromToken, 'USD', defaultBlock)

  return {
    getTokenRate,
    getUsdRate
  }
}

module.exports = createChainLink
